import { Controller } from "@hotwired/stimulus"

const SELECTORS = {
  choiceGroup: "[data-career-assistant-choice-group]",
  planOption: "[data-career-assistant-plan-option]",
  buildPlanButton: "[data-career-assistant-build-plan-button]",
  positionCard: "[data-career-assistant-position-card]",
  details: "[data-opportunity-field='details']",
  toggleLabel: "[data-opportunity-field='toggle_label']",
  toggleIcon: "[data-opportunity-field='toggle_icon']",
  addInterestForm: "[data-career-assistant-add-interest-form]",
  addInterestRoleGroup: "[data-career-assistant-drawer-target~='addInterestRoleGroup']",
  addInterestRoleSelect: "[data-career-assistant-drawer-target~='addInterestRoleSelect']",
  addInterestIndustrySelect: "[data-career-assistant-drawer-target~='addInterestIndustrySelect']",
  addInterestSubmitButton: "[data-career-assistant-drawer-target~='addInterestSubmitButton']"
}

export default class extends Controller {
  static targets = ["messages", "typingIndicator", "sessionId", "useCase", "buildPlanButton", "addInterestRoleGroup", "addInterestRoleSelect", "addInterestIndustrySelect", "addInterestSubmitButton", "autoRequestMessage", "matchingJobIds"]

  static values = {
    chatUrl: String,
    saveProfileUrl: String
  }

  connect() {
    this.loading = false
    this.initialMessagesMarkup = this.messagesTarget.innerHTML
    this.initialUseCase = this.#currentUseCase()
    this.initialMatchingJobIds = this.#currentMatchingJobIds()
    this.handleTurboSubmitStart = this.handleTurboSubmitStart.bind(this)
    this.handleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    this.handleTurboBeforeFetchRequest = this.handleTurboBeforeFetchRequest.bind(this)

    this.element.addEventListener("turbo:submit-start", this.handleTurboSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    this.element.addEventListener("turbo:before-fetch-request", this.handleTurboBeforeFetchRequest)

    this.#setLoading(false)
    this.#scrollMessagesToBottom()
    this.#observeMessagesMutations()
    this.#updateBuildPlanButtons()
    this.#submitInitialAutoRequest()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this.handleTurboSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
    this.element.removeEventListener("turbo:before-fetch-request", this.handleTurboBeforeFetchRequest)
    this.messagesObserver?.disconnect()
  }

  selectChoice(event) {
    event.preventDefault()

    const button = event.currentTarget
    const form = button.form
    const choiceGroup = button.closest(SELECTORS.choiceGroup)

    if (!form || !choiceGroup) return

    this.#completeChoiceGroup(choiceGroup)
    form.requestSubmit(button)
  }

  restart() {
    this.messagesTarget.innerHTML = this.initialMessagesMarkup
    this.sessionIdTarget.value = ""
    this.useCaseTarget.value = this.initialUseCase
    this.matchingJobIdsTarget.value = this.initialMatchingJobIds
    this.element.dataset.autoRequestSubmitted = "false"

    this.#setLoading(false)
    this.#scrollMessagesToBottom()
    this.#submitInitialAutoRequest()
  }

  handleTurboSubmitStart(event) {
    if (!this.#isDrawerFormEvent(event)) return

    const refreshingCard = this.#refreshingCard(event.target)
    if (refreshingCard) {
      this.#hideGlobalSpinner()
      this.#setCardRefreshing(refreshingCard, true)
      return
    }

    const choiceGroup = event.target.closest(SELECTORS.choiceGroup)
    if (choiceGroup) this.#completeChoiceGroup(choiceGroup)

    this.#hideGlobalSpinner()
    this.#setLoading(true)
    this.#stickMessagesToBottom()
  }

  handleTurboSubmitEnd(event) {
    if (!this.#isDrawerFormEvent(event)) return

    const refreshingCard = this.#refreshingCard(event.target)
    if (refreshingCard) {
      this.#setCardRefreshing(refreshingCard, false)
      return
    }

    const form = event.target
    if (form instanceof HTMLFormElement && form.dataset.ephemeralChoice === "true") {
      form.remove()
    }

    const response = event.detail.fetchResponse?.response
    if (response?.redirected) {
      window.location.reload()
      return
    }

    this.#setLoading(false)
    this.#stickMessagesToBottom()
  }

  handleTurboBeforeFetchRequest(event) {
    if (!this.#isDrawerFormEvent(event)) return

    // Prevent document-level handlers from forcing the fullscreen spinner.
    event.stopPropagation()
    this.#hideGlobalSpinner()
  }

  sendChoice(event) {
    event.preventDefault()

    const button = event.currentTarget
    const message = button?.dataset?.choiceMessage?.toString().trim()
    if (!message || !this.hasChatUrlValue) return

    const displayMessage = button.textContent?.toString().trim()
    const choiceUseCase = button.dataset.choiceUseCase?.toString().trim()
    if (choiceUseCase && this.hasUseCaseTarget) {
      this.useCaseTarget.value = choiceUseCase
    }

    this.#completeChoiceGroup(button.closest(SELECTORS.choiceGroup))
    this.#submitChoiceForm(message, { display_message: displayMessage })
  }

  toggleCard(event) {
    event.preventDefault()
    if (this.loading) return

    const button = event.currentTarget
    const card = button.closest(SELECTORS.positionCard)
    if (!card) return

    const details = card.querySelector(SELECTORS.details)
    if (!details) return

    const isHidden = details.hidden || details.classList.contains("tw-hidden")
    const willExpand = isHidden

    details.hidden = !willExpand
    details.classList.toggle("tw-hidden", !willExpand)
    card.classList.toggle("is-expanded", willExpand)

    const label = button.querySelector(SELECTORS.toggleLabel)
    if (label) label.textContent = willExpand ? "Show less" : "Tell me more"

    const icon = button.querySelector(SELECTORS.toggleIcon)
    if (icon) icon.textContent = willExpand ? "expand_more" : "expand_less"

    button.setAttribute("aria-expanded", willExpand ? "false" : "true")
  }

  togglePlanOption(event) {
    event.preventDefault()
    if (this.loading) return

    const option = event.currentTarget
    if (!(option instanceof HTMLElement)) return

    const selected = !option.classList.contains("is-selected")
    option.classList.toggle("is-selected", selected)
    option.setAttribute("aria-pressed", selected ? "true" : "false")

    option.classList.toggle("tw-border-blue-500", selected)
    option.classList.toggle("tw-shadow-[0_0_0_1px_rgba(44,123,229,0.18)]", selected)

    const checkbox = option.querySelector("[data-plan-option-field='checkbox']")
    if (checkbox instanceof HTMLElement) {
      checkbox.classList.toggle("tw-border-blue-500", selected)
      checkbox.classList.toggle("tw-bg-blue-500", selected)
    }

    const checkmark = option.querySelector("[data-plan-option-field='checkmark']")
    if (checkmark instanceof HTMLElement) {
      checkmark.classList.toggle("tw-hidden", !selected)
    }

    this.#updateBuildPlanButtons()
  }

  submitCareerPlanSelection(event) {
    event.preventDefault()

    const button = event.currentTarget
    if (!(button instanceof HTMLElement)) return
    if (button.hasAttribute("disabled")) return

    const selectedPlanOptions = this.#selectedPlanOptions()
    if (selectedPlanOptions.length === 0) return

    const selectedDirections = selectedPlanOptions.map((option) =>
      [option.industry, option.specialization].filter(Boolean).join(" and ")
    )
    const message = `Build me a right now career plan for ${selectedDirections.join(", ")}`
    this.#submitChoiceForm(message, { selected_plan_options: selectedPlanOptions })
  }

  submitBigDreamGoal(event) {
    this.#submitChoiceForm(event.detail.value)
  }

  updateAddInterestRoleOptions(event) {
    const industrySelect = event.currentTarget
    const form = industrySelect.closest(SELECTORS.addInterestForm)
    const roleSelect = form.querySelector(SELECTORS.addInterestRoleSelect)
    const roleGroup = form.querySelector(SELECTORS.addInterestRoleGroup)

    const selectedIndustryId = industrySelect.value?.toString().trim()
    const placeholder = industrySelect.dataset.addInterestRolePlaceholder?.toString().trim() || "Role"
    const optionsMap = this.#parseJsonMap(industrySelect.dataset.addInterestRoleOptionsMap)
    const roles = selectedIndustryId ? optionsMap[selectedIndustryId] || [] : []

    roleSelect.innerHTML = ""
    roleSelect.appendChild(new Option(placeholder, ""))

    roles.forEach((roleOption) => {
      if (!Array.isArray(roleOption) || roleOption.length < 2) return
      roleSelect.appendChild(new Option(roleOption[0].toString(), roleOption[1].toString()))
    })

    const hasRoles = roles.length > 0
    roleSelect.disabled = !hasRoles
    roleSelect.value = ""
    roleSelect.classList.remove("filled")
    roleGroup.classList.toggle("tw-hidden", !selectedIndustryId)
    this.#toggleAddInterestSubmitButton(false, form)
  }

  toggleAddInterestSubmit(event) {
    const roleSelect = event.currentTarget

    const hasRole = !!roleSelect.value?.toString().trim()
    this.#toggleAddInterestSubmitButton(
      hasRole,
      roleSelect.closest(SELECTORS.addInterestForm)
    )
  }

  submitAddInterest(event) {
    event.preventDefault()

    const button = event.currentTarget
    if (button.hasAttribute("disabled")) return

    const form = button.closest(SELECTORS.addInterestForm)
    const industrySelect = form.querySelector(SELECTORS.addInterestIndustrySelect)
    const roleSelect = form.querySelector(SELECTORS.addInterestRoleSelect)

    const industryLabel = industrySelect.options[industrySelect.selectedIndex]?.text?.toString().trim()
    const roleLabel = roleSelect.options[roleSelect.selectedIndex]?.text?.toString().trim()

    if (!industryLabel || !roleLabel) return

    form.remove()

    if (this.hasSaveProfileUrlValue) {
      this.#submitSaveProfileForm({
        field: industryLabel,
        specialization: roleLabel
      })
      return
    }

    const prefix = button.dataset.addInterestMessagePrefix?.toString().trim() || "Add to my profile"
    const message = `${prefix}: ${roleLabel} (${industryLabel})`
    this.#submitChoiceForm(message)
  }

  #toggleAddInterestSubmitButton(visible, form) {
    const button = form.querySelector(SELECTORS.addInterestSubmitButton)
    button.classList.toggle("tw-hidden", !visible)
  }

  #submitChoiceForm(message, extras = {}) {
    const skipCandidateMessage = String(extras.skip_candidate_message ?? "false")
    const sanitizedExtras = Object.fromEntries(
      Object.entries(extras).filter(([key]) => key !== "skip_candidate_message")
    )

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.chatUrlValue
    form.dataset.turboStream = "true"
    form.dataset.ephemeralChoice = "true"
    form.classList.add("tw-hidden")

    this.#appendHiddenInput(form, "message", message)
    this.#appendHiddenInput(form, "skip_candidate_message", skipCandidateMessage)
    this.#appendHiddenInput(form, "session_id", this.#currentSessionId())
    this.#appendHiddenInput(form, "use_case", this.#currentUseCase())
    this.#appendHiddenInput(form, "matching_job_ids", this.#currentMatchingJobIds())
    this.#appendExtraInputs(form, sanitizedExtras)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) this.#appendHiddenInput(form, "authenticity_token", csrfToken)

    this.element.appendChild(form)
    form.requestSubmit()
  }

  #submitInitialAutoRequest() {
    if (!this.hasAutoRequestMessageTarget) return
    if (this.element.dataset.autoRequestSubmitted === "true") return
    if (!this.hasChatUrlValue) return

    const autoRequestMessage = this.autoRequestMessageTarget.value?.toString().trim()
    if (!autoRequestMessage) return

    const activeSessionId = this.hasSessionIdTarget ? this.sessionIdTarget.value?.toString().trim() : ""
    if (activeSessionId) return

    this.element.dataset.autoRequestSubmitted = "true"
    this.#submitChoiceForm(autoRequestMessage, { skip_candidate_message: "true" })
  }

  #appendHiddenInput(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value?.toString() || ""
    form.appendChild(input)
  }

  #appendExtraInputs(form, extras) {
    Object.entries(extras).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item && typeof item === "object" && !Array.isArray(item)) {
            Object.entries(item).forEach(([subKey, subValue]) => {
              this.#appendHiddenInput(form, `${key}[${index}][${subKey}]`, subValue)
            })
            return
          }

          this.#appendHiddenInput(form, `${key}[]`, item)
        })
        return
      }

      this.#appendHiddenInput(form, key, value)
    })
  }

  #submitSaveProfileForm(saveProfilePayload) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.saveProfileUrlValue
    form.dataset.turboStream = "true"
    form.dataset.ephemeralChoice = "true"
    form.classList.add("tw-hidden")

    this.#appendHiddenInput(form, "action_type", "save")
    this.#appendHiddenInput(form, "session_id", this.#currentSessionId())
    this.#appendHiddenInput(form, "use_case", this.#currentUseCase())
    this.#appendHiddenInput(form, "save_profile[field]", saveProfilePayload.field)
    this.#appendHiddenInput(form, "save_profile[specialization]", saveProfilePayload.specialization)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) this.#appendHiddenInput(form, "authenticity_token", csrfToken)

    this.element.appendChild(form)
    form.requestSubmit()
  }

  #setLoading(loading) {
    this.loading = loading

    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.toggle("tw-hidden", !loading)
    }
  }

  #refreshingCard(form) {
    if (form.dataset.careerAssistantCardRefresh !== "true") return null

    return form.closest(SELECTORS.positionCard)
  }

  #setCardRefreshing(card, refreshing) {
    this.loading = refreshing
    card.setAttribute("aria-busy", refreshing.toString())
    card.querySelector("[data-opportunity-card-loading]").hidden = !refreshing
    card.querySelectorAll("button").forEach((button) => {
      button.disabled = refreshing
    })
  }

  #completeChoiceGroup(group) {
    if (!(group instanceof HTMLElement)) return

    group.querySelectorAll("button").forEach((button) => {
      button.disabled = true
      button.setAttribute("aria-disabled", "true")
    })
    this.dispatch("started")
  }

  #currentSessionId() {
    return this.hasSessionIdTarget ? this.sessionIdTarget.value : ""
  }

  #currentUseCase() {
    return this.hasUseCaseTarget ? this.useCaseTarget.value : ""
  }

  #currentMatchingJobIds() {
    return this.hasMatchingJobIdsTarget ? this.matchingJobIdsTarget.value : ""
  }

  #scrollMessagesToBottom() {
    this.#scrollContainers().forEach((container) => {
      container.scrollTop = container.scrollHeight
    })
  }

  #stickMessagesToBottom() {
    this.#scrollMessagesToBottom()

    requestAnimationFrame(() => {
      this.#scrollMessagesToBottom()

      requestAnimationFrame(() => {
        this.#scrollMessagesToBottom()
      })
    })

    setTimeout(() => this.#scrollMessagesToBottom(), 40)
    setTimeout(() => this.#scrollMessagesToBottom(), 120)
    setTimeout(() => this.#scrollMessagesToBottom(), 220)
  }

  #scrollContainers() {
    if (!this.hasMessagesTarget) return []

    const candidates = [
      this.messagesTarget.closest(".sidenav-body"),
      document.querySelector("#sidenav_content .sidenav-body"),
      document.getElementById("sidenav_content"),
      this.messagesTarget.closest("#sidenav_content"),
      this.messagesTarget
    ].filter(Boolean)

    return [...new Set(candidates)]
  }

  #observeMessagesMutations() {
    if (!this.hasMessagesTarget || typeof MutationObserver === "undefined") return

    this.messagesObserver?.disconnect()
    this.messagesObserver = new MutationObserver(() => {
      this.#stickMessagesToBottom()
      this.#updateBuildPlanButtons()
    })
    this.messagesObserver.observe(this.messagesTarget, { childList: true, subtree: true })
  }

  #selectedPlanOptionSpecializations() {
    return [...this.element.querySelectorAll(`${SELECTORS.planOption}.is-selected`)]
      .map((element) => element.dataset.planOptionSpecialization?.toString().trim())
      .filter(Boolean)
  }

  #selectedPlanOptions() {
    return [...this.element.querySelectorAll(`${SELECTORS.planOption}.is-selected`)]
      .map((element) => ({
        industry: element.dataset.planOptionIndustry?.toString().trim(),
        specialization: element.dataset.planOptionSpecialization?.toString().trim()
      }))
      .filter((option) => option.specialization)
  }

  #updateBuildPlanButtons() {
    const hasSelection = this.#selectedPlanOptionSpecializations().length > 0
    const buttons = this.hasBuildPlanButtonTarget ?
      this.buildPlanButtonTargets :
      [...this.element.querySelectorAll(SELECTORS.buildPlanButton)]

    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return

      button.toggleAttribute("disabled", !hasSelection)
      button.classList.toggle("tw-bg-blue-50", !hasSelection)
      button.classList.toggle("tw-text-blue-200", !hasSelection)
      button.classList.toggle("tw-cursor-not-allowed", !hasSelection)
      button.classList.toggle("tw-bg-blue-500", hasSelection)
      button.classList.toggle("tw-text-white", hasSelection)
      button.classList.toggle("tw-cursor-pointer", hasSelection)
    })
  }

  #isDrawerFormEvent(event) {
    const target = event.target
    if (!target || !(target instanceof Element)) return false

    if (target.tagName === "FORM") return true
    return !!target.closest("form")
  }

  #hideGlobalSpinner() {
    if (typeof mdFormHelper === "undefined" || typeof mdFormHelper.hideSpinner !== "function") return

    mdFormHelper.hideSpinner()
  }

  #parseJsonMap(value) {
    if (!value) return {}

    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (_error) {
      return {}
    }
  }
}
